import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Button } from '@gtintel/signin/components/common/Button';
import { Input } from '@gtintel/signin/components/common/Input';
import { useAuth } from '@gtintel/signin/hooks/useAuth';
import { useMFA } from '@gtintel/signin/hooks/useMFA';
import { MFAVerifyRequest } from '@gtintel/signin/types/mfa.types';

const schema = yup.object({
  code: yup
    .string()
    .required('Code is required')
    .matches(/^\d{6}$/, 'Code must be 6 digits'),
});

interface MFAVerifyProps {
  temporaryToken: string;
  availableMethods: ('totp' | 'sms')[];
  redirectUrl: string;
  sourceUrl?: string;
  rememberMe: boolean;
}

export const MFAVerify: React.FC<MFAVerifyProps> = ({
  availableMethods,
  redirectUrl,
  sourceUrl,
  rememberMe,
}) => {
  const navigate = useNavigate();
  const { setRedirectUrls } = useAuth();
  const [selectedMethod] = useState<'totp' | 'sms'>(availableMethods[0]);
  const { verifyMFA, loading, error, clearError } = useMFA();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<{ code: string }>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setFocus('code');
    return () => {
      clearError();
    };
  }, [setFocus, clearError]);

  const onSubmit = async (data: { code: string }) => {
    try {
      const request: MFAVerifyRequest = {
        code: data.code,
        method: selectedMethod,
        source_url: sourceUrl,
        remember_me: rememberMe,
      };

      const response = await verifyMFA(request);
      if (response.success) {
        // Store redirect URLs in context if they exist in the response
        if (response.nexus_redirect_url && response.platform_redirect_url) {
          setRedirectUrls({
            nexus: response.nexus_redirect_url,
            platform: response.platform_redirect_url,
          });
        }

        // Check if this is a direct login (no sourceUrl)
        if (!sourceUrl) {
          // Redirect to selection page for direct logins
          navigate('/apps');
        } else {
          // Redirect to original application with token parameters
          window.location.href = response.redirect_url || redirectUrl;
        }
      }
    } catch (err) {
      // Error is already handled in the hook
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gti-navy font-montserrat">
          Verify Your Identity
        </h2>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register('code')}
          type="text"
          label="Verification code"
          placeholder="000000"
          error={errors.code?.message}
          maxLength={6}
          autoComplete="one-time-code"
          className="text-center text-2xl tracking-widest font-montserrat font-semibold"
        />

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-4">
            <div className="text-sm text-red-700 font-montserrat">{error}</div>
          </div>
        )}

        <Button type="submit" fullWidth loading={loading} size="lg">
          Verify Code
        </Button>
      </form>

      <div className="text-center">
        <button
          type="button"
          className="text-sm text-gti-grey hover:text-gti-navy font-montserrat transition-colors duration-200"
          onClick={() => (window.location.href = '/login')}
        >
          Back to login
        </button>
      </div>
    </div>
  );
};
